import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react'
import { useAuth } from './AuthContext'
import { initWorkspace, getWorkspaceId, getMemberType, getMemberRole, getMemberName, getMemberUsername, refreshMemberInfo } from '../api/backend'
import type { MemberRole } from '../api/backend'

// ─── Types ────────────────────────────────────────────────────────────────────

interface WorkspaceState {
  /** Whether workspace init has completed (workspaceId is available) */
  isResolved: boolean
  workspaceId: string | null
  memberType: string | null
  memberRole: MemberRole | null
  memberName: string | null
  memberUsername: string | null
}

interface WorkspaceContextValue extends WorkspaceState {
  refreshMember: () => Promise<void>
}

// ─── Context ──────────────────────────────────────────────────────────────────

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null)

// ─── Provider ─────────────────────────────────────────────────────────────────

export function WorkspaceProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth()
  const [state, setState] = useState<WorkspaceState>({
    isResolved: false,
    workspaceId: null,
    memberType: null,
    memberRole: null,
    memberName: null,
    memberUsername: null,
  })

  // Prevent duplicate calls on StrictMode double-invoke
  const didInitRef = useRef(false)

  const syncStateFromCache = useCallback(() => {
    setState({
      isResolved: true,
      workspaceId: getWorkspaceId(),
      memberType: getMemberType(),
      memberRole: getMemberRole(),
      memberName: getMemberName(),
      memberUsername: getMemberUsername(),
    })
  }, [])

  const refreshMember = useCallback(async () => {
    await refreshMemberInfo()
    syncStateFromCache()
  }, [syncStateFromCache])

  useEffect(() => {
    if (!isAuthenticated) return
    if (didInitRef.current) return

    didInitRef.current = true

    initWorkspace()
      .then(() => {
        syncStateFromCache()
      })
      .catch((err) => {
        console.error('WorkspaceProvider: failed to init workspace:', err)
        didInitRef.current = false
        setState({ isResolved: false, workspaceId: null, memberType: null, memberRole: null, memberName: null, memberUsername: null })
      })
  }, [isAuthenticated, syncStateFromCache])

  // Reset when user logs out
  useEffect(() => {
    if (!isAuthenticated) {
      didInitRef.current = false
      setState({ isResolved: false, workspaceId: null, memberType: null, memberRole: null, memberName: null, memberUsername: null })
    }
  }, [isAuthenticated])

  return (
    <WorkspaceContext.Provider value={{ ...state, refreshMember }}>
      {children}
    </WorkspaceContext.Provider>
  )
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useWorkspace(): WorkspaceContextValue {
  const ctx = useContext(WorkspaceContext)
  if (!ctx) throw new Error('useWorkspace must be used within WorkspaceProvider')
  return ctx
}
